import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Package, Plus, X, ChevronRight, ChevronLeft, Search, Check,
    Loader2, DollarSign, Box, CheckCircle, FileText, AlertCircle,
    Clock, Send, Zap, ShoppingBag, Filter
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';

const ProductQuickAdd = ({ isOpen, onClose, onProductAdded, token }) => {
    const navigate = useNavigate();
    const [mode, setMode] = useState('select'); // 'select', 'catalog', 'request'
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [websiteProducts, setWebsiteProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);

    // For new product request
    const [newProductRequest, setNewProductRequest] = useState({
        name: '',
        category: '',
        description: '',
        brand: '',
        specifications: '',
        expectedPrice: '',
        reason: ''
    });

    const categories = ['Solar Panels', 'Inverters', 'Batteries', 'Mounting Systems', 'Cables & Connectors', 'Complete Kits'];

    // Fetch website catalog products
    useEffect(() => {
        if (isOpen && mode === 'catalog') {
            fetchCatalogProducts();
        }
    }, [isOpen, mode]);

    const fetchCatalogProducts = async () => {
        setCatalogLoading(true);
        try {
            const response = await axios.get(`${API}/api/products`);
            setWebsiteProducts(response.data || []);
        } catch (error) {
            console.error('Failed to fetch products:', error);
            // Fallback demo products
            setWebsiteProducts([
                { id: '1', name: '400W Monocrystalline Solar Panel', category: 'Solar Panels', price: 15000, image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=200', stock: 50 },
                { id: '2', name: '500W Bifacial Solar Panel', category: 'Solar Panels', price: 22000, image: 'https://images.unsplash.com/photo-1558449028-b53a39d100fc?w=200', stock: 30 },
                { id: '3', name: '5kW On-Grid Solar Inverter', category: 'Inverters', price: 45000, image: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=200', stock: 20 },
                { id: '4', name: '3kW Hybrid Inverter', category: 'Inverters', price: 55000, image: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=200', stock: 15 },
                { id: '5', name: '5kWh Lithium Battery', category: 'Batteries', price: 150000, image: 'https://images.unsplash.com/photo-1620714223084-8fcacc6dfd8d?w=200', stock: 10 },
                { id: '6', name: '3kW Complete Home Solar Kit', category: 'Complete Kits', price: 180000, image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=200', stock: 8 },
            ]);
        } finally {
            setCatalogLoading(false);
        }
    };

    const filteredProducts = websiteProducts.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const toggleProductSelection = (product) => {
        setSelectedProducts(prev => {
            const exists = prev.find(p => p.id === product.id);
            if (exists) {
                return prev.filter(p => p.id !== product.id);
            } else {
                return [...prev, { ...product, vendorPrice: '', vendorStock: '10' }];
            }
        });
    };

    const updateProductPricing = (productId, field, value) => {
        setSelectedProducts(prev => prev.map(p =>
            p.id === productId ? { ...p, [field]: value } : p
        ));
    };

    const handleAddFromCatalog = async () => {
        // Validate all selected products have valid prices and stock
        const invalidProducts = selectedProducts.filter(p => {
            const price = parseFloat(p.vendorPrice);
            const stock = parseInt(p.vendorStock, 10);
            return !p.vendorPrice || isNaN(price) || price <= 0 || price > p.price || isNaN(stock) || stock < 0;
        });

        if (invalidProducts.length > 0) {
            toast.error('Please set valid prices (must be > 0 and ≤ website price) and stock (must be ≥ 0)');
            return;
        }

        setLoading(true);
        try {
            for (const product of selectedProducts) {
                await axios.post(`${API}/api/products`, {
                    name: product.name,
                    description: `From catalog: ${product.name}`,
                    category: product.category,
                    price: parseFloat(product.vendorPrice),
                    stock: parseInt(product.vendorStock, 10) || 10,
                    image: product.image,
                    specifications: { catalog_ref: product.id }
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            toast.success(`${selectedProducts.length} product(s) added successfully!`);
            onProductAdded?.();
            handleClose();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add products');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitNewProductRequest = async () => {
        const { name, category, description, expectedPrice } = newProductRequest;

        if (!name || !category || !description || !expectedPrice) {
            toast.error('Please fill all required fields');
            return;
        }

        setLoading(true);
        try {
            // In a real app, this would submit to a review queue
            // For now, simulate submission
            await new Promise(resolve => setTimeout(resolve, 1000));

            toast.success('Product request submitted! Our team will review it within 24-48 hours.');
            handleClose();
        } catch (error) {
            toast.error('Failed to submit request');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setMode('select');
        setStep(1);
        setSelectedProducts([]);
        setNewProductRequest({
            name: '', category: '', description: '', brand: '',
            specifications: '', expectedPrice: '', reason: ''
        });
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
                    onClick={handleClose}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Add product dialog"
                >
                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        className="bg-card rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl overflow-hidden max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-4 border-b flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                    <Package className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h2 className="font-bold">
                                        {mode === 'select' && 'Add Products'}
                                        {mode === 'catalog' && 'Select from Catalog'}
                                        {mode === 'request' && 'Request New Product'}
                                    </h2>
                                    <p className="text-xs text-muted-foreground">
                                        {mode === 'catalog' && `${selectedProducts.length} selected`}
                                        {mode === 'request' && 'Submit for review'}
                                    </p>
                                </div>
                            </div>
                            <button onClick={handleClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4">
                            <AnimatePresence mode="wait">
                                {/* Mode Selection */}
                                {mode === 'select' && (
                                    <motion.div
                                        key="select"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        className="space-y-4"
                                    >
                                        <p className="text-muted-foreground text-sm">
                                            Choose how you want to add products to your store:
                                        </p>

                                        {/* Option 1: From Catalog */}
                                        <button
                                            onClick={() => {
                                                handleClose();
                                                navigate('/vendor/products', { state: { action: 'browse_catalog' } });
                                            }}
                                            className="w-full text-left border-2 border-primary/30 rounded-xl p-5 hover:border-primary hover:bg-primary/5 transition-all group"
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                                                    <ShoppingBag className="w-6 h-6" />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="font-semibold flex items-center gap-2">
                                                        Add from Website Catalog
                                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                                            Recommended
                                                        </span>
                                                    </h3>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        Select products already listed on SolarSavers. Set your price
                                                        (must be ≤ website price) and start selling immediately.
                                                    </p>
                                                    <div className="flex items-center gap-3 mt-3 text-xs">
                                                        <span className="flex items-center gap-1 text-green-600">
                                                            <Zap className="w-3 h-3" /> Instant approval
                                                        </span>
                                                        <span className="flex items-center gap-1 text-muted-foreground">
                                                            <CheckCircle className="w-3 h-3" /> No documents needed
                                                        </span>
                                                    </div>
                                                </div>
                                                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                                            </div>
                                        </button>

                                        {/* Option 2: Request New */}
                                        <button
                                            onClick={() => {
                                                handleClose();
                                                navigate('/vendor/products', { state: { action: 'suggest_product' } });
                                            }}
                                            className="w-full text-left border-2 rounded-xl p-5 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group"
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors">
                                                    <Plus className="w-6 h-6" />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="font-semibold">Request New Product Listing</h3>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        Have a solar product not on our website? Submit a request
                                                        to add it to the catalog.
                                                    </p>
                                                    <div className="flex items-center gap-3 mt-3 text-xs">
                                                        <span className="flex items-center gap-1 text-blue-600">
                                                            <Clock className="w-3 h-3" /> 24-48 hour review
                                                        </span>
                                                        <span className="flex items-center gap-1 text-muted-foreground">
                                                            <FileText className="w-3 h-3" /> Documentation required
                                                        </span>
                                                    </div>
                                                </div>
                                                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-blue-500" />
                                            </div>
                                        </button>
                                    </motion.div>
                                )}

                                {/* Catalog Mode */}
                                {mode === 'catalog' && step === 1 && (
                                    <motion.div
                                        key="catalog-1"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="space-y-4"
                                    >
                                        {/* Filters */}
                                        <div className="flex gap-2">
                                            <div className="relative flex-1">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input
                                                    placeholder="Search products..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    className="pl-9"
                                                />
                                            </div>
                                            <select
                                                value={categoryFilter}
                                                onChange={(e) => setCategoryFilter(e.target.value)}
                                                className="px-3 h-10 rounded-md border bg-background text-sm"
                                            >
                                                <option value="all">All Categories</option>
                                                {categories.map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Product Grid */}
                                        {catalogLoading ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                            </div>
                                        ) : (
                                            <div className="grid gap-3 max-h-[40vh] overflow-y-auto">
                                                {filteredProducts.map((product) => {
                                                    const isSelected = selectedProducts.find(p => p.id === product.id);
                                                    return (
                                                        <button
                                                            key={product.id}
                                                            onClick={() => toggleProductSelection(product)}
                                                            className={`flex items-center gap-3 p-3 border-2 rounded-xl text-left transition-all ${isSelected
                                                                ? 'border-primary bg-primary/5'
                                                                : 'hover:border-muted-foreground/30'
                                                                }`}
                                                        >
                                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                                                                }`}>
                                                                {isSelected && <Check className="w-4 h-4 text-white" />}
                                                            </div>
                                                            <img
                                                                src={product.image}
                                                                alt={product.name}
                                                                className="w-14 h-14 object-cover rounded-lg"
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="font-medium text-sm truncate">{product.name}</h4>
                                                                <p className="text-xs text-muted-foreground">{product.category}</p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="font-bold text-primary">₹{product.price?.toLocaleString()}</p>
                                                                <p className="text-xs text-muted-foreground">Website price</p>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </motion.div>
                                )}

                                {/* Catalog Mode - Step 2: Set Pricing */}
                                {mode === 'catalog' && step === 2 && (
                                    <motion.div
                                        key="catalog-2"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="space-y-4"
                                    >
                                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm">
                                            <div className="flex items-start gap-2">
                                                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                                <p className="text-amber-800 dark:text-amber-300">
                                                    <strong>Important:</strong> Your price must be ≤ the website price.
                                                    You earn the difference!
                                                </p>
                                            </div>
                                        </div>

                                        <div className="space-y-3 max-h-[40vh] overflow-y-auto">
                                            {selectedProducts.map((product) => (
                                                <div
                                                    key={product.id}
                                                    className="border rounded-xl p-4 space-y-3"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <img
                                                            src={product.image}
                                                            alt={product.name}
                                                            className="w-12 h-12 object-cover rounded-lg"
                                                        />
                                                        <div className="flex-1">
                                                            <h4 className="font-medium text-sm">{product.name}</h4>
                                                            <p className="text-xs text-muted-foreground">
                                                                Max price: <span className="font-medium text-primary">₹{product.price?.toLocaleString()}</span>
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div className="space-y-1">
                                                            <Label className="text-xs">Your Price (₹)</Label>
                                                            <div className="relative">
                                                                <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                                <Input
                                                                    type="number"
                                                                    placeholder={product.price?.toString()}
                                                                    value={product.vendorPrice}
                                                                    onChange={(e) => updateProductPricing(product.id, 'vendorPrice', e.target.value)}
                                                                    className={`pl-7 ${product.vendorPrice && parseFloat(product.vendorPrice) > product.price
                                                                        ? 'border-red-500 focus:ring-red-500'
                                                                        : ''
                                                                        }`}
                                                                />
                                                            </div>
                                                            {product.vendorPrice && parseFloat(product.vendorPrice) > product.price && (
                                                                <p className="text-xs text-red-500">Must be ≤ ₹{product.price}</p>
                                                            )}
                                                        </div>
                                                        <div className="space-y-1">
                                                            <Label className="text-xs">Stock Qty</Label>
                                                            <div className="relative">
                                                                <Box className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                                <Input
                                                                    type="number"
                                                                    placeholder="10"
                                                                    value={product.vendorStock}
                                                                    onChange={(e) => updateProductPricing(product.id, 'vendorStock', e.target.value)}
                                                                    className="pl-7"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}

                                {/* Request New Product Mode */}
                                {mode === 'request' && (
                                    <motion.div
                                        key="request"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="space-y-4"
                                    >
                                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                                            <p className="text-blue-800 dark:text-blue-300">
                                                Submit your product for review. Once approved, it will be added to the
                                                catalog and you can start selling.
                                            </p>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="space-y-1">
                                                    <Label className="text-sm">Product Name *</Label>
                                                    <Input
                                                        placeholder="e.g., 550W Solar Panel"
                                                        value={newProductRequest.name}
                                                        onChange={(e) => setNewProductRequest({ ...newProductRequest, name: e.target.value })}
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-sm">Category *</Label>
                                                    <select
                                                        value={newProductRequest.category}
                                                        onChange={(e) => setNewProductRequest({ ...newProductRequest, category: e.target.value })}
                                                        className="w-full h-10 px-3 rounded-md border bg-background"
                                                    >
                                                        <option value="">Select</option>
                                                        {categories.map(cat => (
                                                            <option key={cat} value={cat}>{cat}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="space-y-1">
                                                <Label className="text-sm">Brand Name</Label>
                                                <Input
                                                    placeholder="e.g., Tata Solar, Adani, Luminous"
                                                    value={newProductRequest.brand}
                                                    onChange={(e) => setNewProductRequest({ ...newProductRequest, brand: e.target.value })}
                                                />
                                            </div>

                                            <div className="space-y-1">
                                                <Label className="text-sm">Description *</Label>
                                                <Textarea
                                                    placeholder="Describe the product..."
                                                    value={newProductRequest.description}
                                                    onChange={(e) => setNewProductRequest({ ...newProductRequest, description: e.target.value })}
                                                    rows={3}
                                                />
                                            </div>

                                            <div className="space-y-1">
                                                <Label className="text-sm">Expected Price (₹) *</Label>
                                                <Input
                                                    type="number"
                                                    placeholder="Your selling price"
                                                    value={newProductRequest.expectedPrice}
                                                    onChange={(e) => setNewProductRequest({ ...newProductRequest, expectedPrice: e.target.value })}
                                                />
                                            </div>

                                            <div className="space-y-1">
                                                <Label className="text-sm">Why should we add this?</Label>
                                                <Textarea
                                                    placeholder="Explain why this product would be valuable..."
                                                    value={newProductRequest.reason}
                                                    onChange={(e) => setNewProductRequest({ ...newProductRequest, reason: e.target.value })}
                                                    rows={2}
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t bg-muted/30">
                            <div className="flex justify-between">
                                {mode === 'select' ? (
                                    <Button variant="ghost" onClick={handleClose}>Cancel</Button>
                                ) : (
                                    <Button
                                        variant="ghost"
                                        onClick={() => {
                                            if (mode === 'catalog' && step === 2) {
                                                setStep(1);
                                            } else {
                                                setMode('select');
                                                setStep(1);
                                            }
                                        }}
                                        className="gap-1"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                        Back
                                    </Button>
                                )}

                                {mode === 'catalog' && step === 1 && (
                                    <Button
                                        onClick={() => setStep(2)}
                                        disabled={selectedProducts.length === 0}
                                        className="gap-1"
                                    >
                                        Set Prices ({selectedProducts.length})
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                )}

                                {mode === 'catalog' && step === 2 && (
                                    <Button
                                        onClick={handleAddFromCatalog}
                                        disabled={loading}
                                        className="gap-1 bg-green-600 hover:bg-green-700"
                                    >
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                        Add {selectedProducts.length} Product(s)
                                    </Button>
                                )}

                                {mode === 'request' && (
                                    <Button
                                        onClick={handleSubmitNewProductRequest}
                                        disabled={loading}
                                        className="gap-1"
                                    >
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                        Submit Request
                                    </Button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ProductQuickAdd;
